import {useState, useEffect} from "react"
import supabase from "@/supabaseConfig"
import {AlertDefinition} from "@/features/alerts/alertTypes"
import {AlertCategory, AlertDefinitionMetadata} from "@/types/database.types"
import {useUser} from "@/contexts/UserContextProvider"

interface GroupedAlertDefinitions {
  category: AlertCategory
  definitions: (AlertDefinition & {
    metadata: AlertDefinitionMetadata
  })[]
}

// Interface for creating a new alert definition
export interface CreateAlertDefinitionInput {
  name: string
  title?: string
  description?: string
  metadata: AlertDefinitionMetadata
  active?: boolean
}

export function useAlertDefinitions() {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | undefined>()
  const [definitions, setDefinitions] = useState<GroupedAlertDefinitions[]>([])
  const [creating, setCreating] = useState(false)
  const [createError, setCreateError] = useState<string | undefined>()

  // Get orgId from user context instead of querying database
  const {orgId} = useUser()

  /**
   * Parse JSON metadata safely
   */
  const parseMetadata = (metadata: any): AlertDefinitionMetadata => {
    try {
      if (typeof metadata === "string") {
        return JSON.parse(metadata)
      }
      return metadata as AlertDefinitionMetadata
    } catch (error) {
      console.warn("Failed to parse metadata:", error)
      return {
        riskLevel: "medium",
        category: "non_compliant_communications",
        positiveExamples: [],
        negativeExamples: [],
        llmGuidance: "",
        tags: ["all_messages"],
      }
    }
  }

  const fetchAlertDefinitions = async () => {
    try {
      setLoading(true)
      setError(undefined)

      if (!orgId) throw new Error("No organization found for user")

      // Fetch alert definitions for this organization or global ones
      const {data: alertDefinitions, error: fetchError} = await supabase
        .from("alert_definitions")
        .select("*")
        .or(`org_id.eq.${orgId},scope.eq.global`)
        .eq("active", true)
        .order("name")

      if (fetchError) throw fetchError

      // Parse and group by category
      const parsedDefinitions = (alertDefinitions || []).map((def) => ({
        ...def,
        metadata: parseMetadata(def.metadata),
      }))

      // Group by category
      const grouped = parsedDefinitions.reduce((acc, def) => {
        const category = def.metadata.category || "non_compliant_communications"

        let group = acc.find((g) => g.category === category)
        if (!group) {
          group = {
            category,
            definitions: [],
          }
          acc.push(group)
        }

        group.definitions.push(def)
        return acc
      }, [] as GroupedAlertDefinitions[])

      // Sort categories by a preferred order
      const categoryOrder: AlertCategory[] = [
        "non_compliant_communications",
        "advertising_marketing_violations",
        "client_investor_protection",
        "personal_trading_employee_conduct",
        "behavioral_sentiment_analysis",
      ]

      grouped.sort((a, b) => {
        const aIndex = categoryOrder.indexOf(a.category)
        const bIndex = categoryOrder.indexOf(b.category)
        return aIndex - bIndex
      })

      setDefinitions(grouped)
    } catch (err) {
      console.error("Error fetching alert definitions:", err)
      setError(
        err instanceof Error ? err.message : "Failed to load alert definitions"
      )
    } finally {
      setLoading(false)
    }
  }

  const createAlertDefinition = async (
    input: CreateAlertDefinitionInput
  ): Promise<AlertDefinition | null> => {
    try {
      setCreating(true)
      setCreateError(undefined)

      if (!orgId) {
        throw new Error("No organization found for user")
      }

      // Prepare the insert data
      const insertData = {
        name: input.name,
        title: input.title || input.name,
        description: input.description,
        metadata: input.metadata,
        org_id: orgId, // RLS will ensure only org admins can create alerts for their org
        scope: "org_specific", // Always create org-specific alerts
        active: input.active ?? true,
      }

      // Create the alert definition using supabase client
      const {data, error: insertError} = await supabase
        .from("alert_definitions")
        .insert(insertData)
        .select()
        .single()

      if (insertError) {
        throw new Error(
          `Failed to create alert definition: ${insertError.message}`
        )
      }

      if (!data) {
        throw new Error("No data returned after creating alert definition")
      }

      // Refresh the definitions list to include the new alert
      await fetchAlertDefinitions()

      return data as AlertDefinition
    } catch (err) {
      console.error("Error creating alert definition:", err)
      const errorMessage =
        err instanceof Error ? err.message : "Failed to create alert definition"
      setCreateError(errorMessage)
      return null
    } finally {
      setCreating(false)
    }
  }

  useEffect(() => {
    // Only fetch when orgId is available
    if (orgId) {
      fetchAlertDefinitions()
    }
  }, [orgId])

  return {
    loading,
    error,
    definitions,
    refetch: fetchAlertDefinitions,
    createAlertDefinition,
    creating,
    createError,
  }
}
